import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TableComponent from '../../components/TableComponent';
import AppHeader from '../../components/AppHeader';
import DottedCircleLoader from '../../components/DottedCircleLoader';
import CandidateDetail from '../chat/CandidateDetail';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { getMetadata } from '../../utils/encryptedStorage';
import { useTheme } from '../../contexts/ThemeContext';

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRangeForFilter = (filter, customDateRange) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (filter === 'Today') {
    const value = toYmd(today);
    return { created_from: value, created_to: value };
  }

  if (filter === 'Yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const value = toYmd(yesterday);
    return { created_from: value, created_to: value };
  }

  if (filter === 'Last 7 Days') {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return { created_from: toYmd(from), created_to: toYmd(today) };
  }

  if (filter === 'Last 30 Days') {
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return { created_from: toYmd(from), created_to: toYmd(today) };
  }

  if (filter === 'Last Year') {
    const from = new Date(today);
    from.setFullYear(from.getFullYear() - 1);
    from.setDate(from.getDate() + 1);
    return { created_from: toYmd(from), created_to: toYmd(today) };
  }

  if (filter === 'Custom' && customDateRange?.from && customDateRange?.to) {
    return {
      created_from: customDateRange.from,
      created_to: customDateRange.to,
    };
  }

  return {};
};

const normalizeCandidatesPayload = (response, fallbackLimit = 10) => {
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    return {
      page: Number(response.page || 1),
      limit: Number(response.limit || fallbackLimit || 10),
      total_records: Number(response.total_records || 0),
      total_pages: Number(response.total_pages || 1),
      job_counts: Array.isArray(response.job_counts) ? response.job_counts : [],
      data: Array.isArray(response.data) ? response.data : [],
    };
  }

  const rows = Array.isArray(response) ? response : [];
  return {
    page: 1,
    limit: Number(fallbackLimit || 10),
    total_records: rows.length,
    total_pages: 1,
    job_counts: [],
    data: rows,
  };
};

const normalizeOverviewStatus = (status) => String(status || 'pending').trim().toLowerCase();

const getOverviewStatusConfig = (status) => {
  const s = normalizeOverviewStatus(status);
  const config = {
    sourced: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', label: 'Sourced' },
    contacted: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Contacted' },
    interested: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Interested' },
    shortlisted: { bgStyle: '#E6FCF5', textStyle: '#20C997', label: 'Shortlisted', custom: true },
    interviewed: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interviewed' },
    declined: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'Declined' },
    rejected: { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-400', label: 'Rejected' },
    identified: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', label: 'Sourced' },
    responded: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Interested' },
    interview: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interview' },
    'interview scheduled': { bgStyle: '#E6FCF5', textStyle: '#20C997', label: 'Shortlisted', custom: true },
    finalised: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interviewed' },
    finalized: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interviewed' },
    pending: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', label: 'Sourced' },
  };
  return config[s] || config.pending;
};

const getOverviewNextStep = (status) => {
  const s = normalizeOverviewStatus(status);
  if (s === 'interested') return 'Schedule Interview';
  if (s === 'shortlisted') return 'Attend Interview';
  if (s === 'interviewed' || s === 'finalised' || s === 'finalized') return 'Review Candidate';
  if (s === 'declined' || s === 'rejected') return 'Closed';
  return 'Send Follow-up';
};

const MasterDatabase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [selectedFilter, setSelectedFilter] = useState('');
  const openedFromDashboard = useMemo(() => new URLSearchParams(location.search).get('from_dashboard') === 'true', [location.search]);
  const handleBackNavigation = useCallback(() => {
    if (openedFromDashboard) {
      navigate('/dashboard');
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/dashboard');
  }, [navigate, openedFromDashboard]);
  const resolveCompanyId = useCallback(() => {
    const metadata = getMetadata() || {};
    const fallbackIds = [
      metadata?.cid,
      metadata?.company_id,
      metadata?.companyId,
      localStorage.getItem('cid'),
      localStorage.getItem('company_id'),
      localStorage.getItem('companyId'),
    ];

    const selectedId = fallbackIds.find((value) => {
      if (value === undefined || value === null) return false;
      return String(value).trim() !== '';
    });

    return selectedId ? String(selectedId) : '';
  }, []);

  const filters = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'Last Year', 'Custom'];
  const tabs = ['All', 'Interested', 'Shortlisted', 'Interviewed', 'Finalized', 'Rejected'];

  const [candidates, setCandidates] = useState([]);
  const [totalCandidatesCount, setTotalCandidatesCount] = useState(0);
  const [shortlistedCount, setShortlistedCount] = useState(0);
  const [highMatchQualityCount, setHighMatchQualityCount] = useState(0);
  const [jobCounts, setJobCounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJobPostingFilter, setSelectedJobPostingFilter] = useState('');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const [tableCurrentPage, setTableCurrentPage] = useState(1);
  const isCustomDateRangeReady = selectedFilter !== 'Custom' || Boolean(customDateRange.from && customDateRange.to);

  const jobFilterOptions = useMemo(() => {
    const map = new Map();
    (candidates || []).forEach((c) => {
      const job = String(c.headline || c.title || '').trim();
      if (!job) return;
      map.set(job, (map.get(job) || 0) + 1);
    });
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([job]) => ({ label: job, value: job }));
  }, [candidates]);

  // Preprocess candidate data to match CandidateList design
  const preprocessCandidates = (rawCandidates) => {
    const parseArrayField = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };

    const parseObjectField = (value) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch {
          return {};
        }
      }
      return {};
    };

    return (rawCandidates || []).map((c) => {
      const payload = parseObjectField(c.full_candidate_data);
      const tagArrayFromText = typeof c.tags === 'string'
        ? c.tags.split(',').map((item) => item.trim()).filter(Boolean)
        : [];

      const jdTags = tagArrayFromText.length > 0
        ? tagArrayFromText
        : (parseArrayField(payload.tags).length > 0 ? parseArrayField(payload.tags) : []);

      const candidateSkills = parseArrayField(c.skills).length > 0
        ? parseArrayField(c.skills)
        : (parseArrayField(payload.skills).length > 0 ? parseArrayField(payload.skills) : []);

      const skills = jdTags.length > 0 ? jdTags : candidateSkills;

      const experience = parseArrayField(c.experience).length > 0
        ? parseArrayField(c.experience)
        : parseArrayField(payload.experience);

      const platforms = parseArrayField(c.platforms).length > 0
        ? parseArrayField(c.platforms)
        : (typeof c.platforms === 'string' && c.platforms.trim() ? [c.platforms] : parseArrayField(payload.platforms));

      const headline = c.headline_text || c.headline || payload.headline || payload.current_role || payload.current_position || c.current_role || payload.title || '';
      const currentCompany = c.current_company || payload.current_company || payload.company || '';
      const status = c.funnel_stage || c.status || c.stage || c.candidate_status || c.pipeline_status || payload.funnel_stage || payload.status || 'pending';
      const phone = c.phone || c.mobile_number || payload.phone || payload.mobile_number || '';
      const parsedMatchBreakdown = parseObjectField(c.match_breakdown);
      const parsedPayloadMatchBreakdown = parseObjectField(payload.match_breakdown);
      const highlights = parseArrayField(c.highlights).length > 0
        ? parseArrayField(c.highlights)
        : parseArrayField(payload.highlights);

      return {
        id: c.id || `candidate-${c.email}`,
        name: c.name || payload.name || 'Unknown',
        title: c.headline || c.current_role || payload.headline || payload.current_role || payload.current_position || payload.title || '',
        company: currentCompany,
        current_company: currentCompany,
        current_role: c.current_role || payload.current_role || '',
        current_position: c.current_position || payload.current_position || '',
        location: c.location || payload.location || '',
        email: c.email || '',
        phone: phone,
        mobile_number: phone,
        linkedin_profile_url: c.linkedin_profile_url || payload.linkedin_profile_url || c.profile_url || payload.profile_url || '',
        profile_url: c.profile_url || payload.profile_url || c.linkedin_profile_url || payload.linkedin_profile_url || '',
        profile_photo: c.profile_photo || c.profile_image || payload.profile_image || c.photo_url || c.image_url || c.avatar_url || '',
        match_percentage: c.match_percentage || null,
        match_breakdown: Object.keys(parsedMatchBreakdown).length > 0 ? parsedMatchBreakdown : parsedPayloadMatchBreakdown,
        skills: skills,
        tags: skills,
        experience_years: c.experience_years || c.years_of_experience || payload.experience_years || payload.years_of_experience || '',
        years_of_experience: c.years_of_experience || c.experience_years || payload.years_of_experience || payload.experience_years || '',
        education: c.education_records || c.education || [],
        education_records: c.education_records || c.education || [],
        platforms: platforms,
        source: platforms[0] || c.source || c.source_type || '',
        source_type: c.source_type || payload.source_type || '',
        _source_type: c._source_type || c.source_type || payload.source_type || '',
        status: status,
        funnel_stage: status,
        candidate_status: c.candidate_status || status,
        pipeline_status: c.pipeline_status || status,
        rejection_reason: c.rejection_reason || payload.rejection_reason || '',
        priority: Boolean(c.priority || payload.priority),
        updatedAt: c.updated_at || c.created_at || new Date().toISOString(),
        createdAt: c.created_at || c.updated_at || null,
        summary: c.summary || c.summary_text || '',
        summary_text: c.summary_text || c.summary || '',
        highlights: highlights,
        headline: headline,
        experience: experience,
        lastAction: c.last_action || 'Outreach sent',
        nextStep: c.next_step || getOverviewNextStep(status),
        doc_id: c.doc_id || null,
        cv_filename: c.cv_filename || null,
        session_id: c.session_id || null,
        search_id: c.search_id || null,
      };
    });
  };

  const fetchCandidatesServer = useCallback(async ({
    page,
    limit,
    search,
    sort_by,
    sort_order,
    job_filter,
    statusTabFilter,
    filters: columnFilters,
  }) => {
    if (!isCustomDateRangeReady) {
      return {
        page: Number(page || 1),
        limit: Number(limit || 10),
        total_records: 0,
        total_pages: 1,
        data: [],
      };
    }

    const companyId = resolveCompanyId();
    if (!companyId) {
      return {
        page: Number(page || 1),
        limit: Number(limit || 10),
        total_records: 0,
        total_pages: 1,
        data: [],
      };
    }

    const sortKeyMap = {
      name: 'name',
      match_percentage: 'match_percentage',
      platforms: 'source',
      status: 'status',
      resume_stats: 'resume_status',
      experience: 'experience',
      tags: 'tags',
      headline: 'job_posting',
      lastAction: 'updated_at',
      nextStep: 'status',
      contact: 'contact',
      lastUpdated: 'updated_at',
    };

    const filterKeyMap = {
      match_percentage: 'match',
      platforms: 'source',
      status: 'status',
      resume_stats: 'resume_status',
      experience: 'experience',
      tags: 'tags',
      headline: 'job_posting',
      lastAction: 'last_action',
      nextStep: 'next_step',
      contact: 'contact',
      lastUpdated: 'updated_at',
    };

    const params = new URLSearchParams();
    params.set('page', String(page || 1));
    params.set('limit', String(limit || 10));
    params.set('sort_by', sortKeyMap[sort_by] || 'match_percentage');
    params.set('sort_order', String(sort_order || 'desc'));
    params.set('cid', companyId);
    const dateRange = getDateRangeForFilter(selectedFilter, customDateRange);
    if (dateRange.created_from) params.set('created_from', dateRange.created_from);
    if (dateRange.created_to) params.set('created_to', dateRange.created_to);

    if (search) params.set('search', String(search));

    if (statusTabFilter?.key && statusTabFilter?.value && String(statusTabFilter.value).toLowerCase() !== 'all') {
      params.set(statusTabFilter.key, String(statusTabFilter.value));
    }

    if (job_filter) params.set('job_posting', String(job_filter));

    Object.entries(columnFilters || {}).forEach(([k, v]) => {
      const value = String(v || '').trim();
      if (!value) return;
      const apiKey = filterKeyMap[k] || k;
      params.set(apiKey, value);
    });

    const response = await aiAutomationApi.get(`/automation_candidates/all?${params.toString()}`);
    const payload = response && typeof response === 'object' && !Array.isArray(response)
      ? response
      : { page: 1, limit: limit || 10, total_records: Array.isArray(response) ? response.length : 0, total_pages: 1, data: Array.isArray(response) ? response : [] };

    return {
      page: payload.page || Number(page || 1),
      limit: payload.limit || Number(limit || 10),
      total_records: payload.total_records || 0,
      total_pages: payload.total_pages || 1,
      data: preprocessCandidates(payload.data || []),
    };
  }, [selectedFilter, customDateRange, isCustomDateRangeReady, resolveCompanyId]);
  useEffect(() => {
    if (!isCustomDateRangeReady) {
      setCandidates([]);
      setTotalCandidatesCount(0);
      setShortlistedCount(0);
      setHighMatchQualityCount(0);
      setJobCounts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchCandidates = async () => {
      try {
        const companyId = resolveCompanyId();
        if (!companyId) {
          setCandidates([]);
          setTotalCandidatesCount(0);
          setShortlistedCount(0);
          setHighMatchQualityCount(0);
          setJobCounts([]);
          return;
        }

        const initialLimit = 10;
        const analyticsLimit = 200;
        const dateRange = getDateRangeForFilter(selectedFilter, customDateRange);
        const baseParams = new URLSearchParams({ page: '1', limit: String(initialLimit) });
        baseParams.set('cid', companyId);
        if (dateRange.created_from) baseParams.set('created_from', dateRange.created_from);
        if (dateRange.created_to) baseParams.set('created_to', dateRange.created_to);
        const shortlistedParams = new URLSearchParams(baseParams);
        shortlistedParams.set('status', 'shortlisted');
        shortlistedParams.set('limit', '1');
        const highMatchParams = new URLSearchParams(baseParams);
        highMatchParams.set('match_min', '80');
        highMatchParams.set('limit', '1');

        const [response, shortlistedResponse, highMatchResponse] = await Promise.all([
          aiAutomationApi.get(`/automation_candidates/all?${baseParams.toString()}`),
          aiAutomationApi.get(`/automation_candidates/all?${shortlistedParams.toString()}`),
          aiAutomationApi.get(`/automation_candidates/all?${highMatchParams.toString()}`),
        ]);
        const payload = normalizeCandidatesPayload(response, initialLimit);
        const shortlistedPayload = normalizeCandidatesPayload(shortlistedResponse, 1);
        const highMatchPayload = normalizeCandidatesPayload(highMatchResponse, 1);
        const rows = Array.isArray(payload?.data) ? payload.data : [];

        const totalForAnalytics = Number(payload?.total_records || rows.length || 0);
        let analyticsRows = rows;

        if (totalForAnalytics > rows.length) {
          const analyticsTotalPages = Math.max(1, Math.ceil(totalForAnalytics / analyticsLimit));
          const analyticsRequests = [];

          for (let pageNumber = 1; pageNumber <= analyticsTotalPages; pageNumber += 1) {
            const analyticsParams = new URLSearchParams(baseParams);
            analyticsParams.set('limit', String(analyticsLimit));
            analyticsParams.set('page', String(pageNumber));
            analyticsRequests.push(aiAutomationApi.get(`/automation_candidates/all?${analyticsParams.toString()}`));
          }

          const analyticsResponses = await Promise.all(analyticsRequests);
          analyticsRows = analyticsResponses.flatMap((item) => {
            const normalized = normalizeCandidatesPayload(item, analyticsLimit);
            return Array.isArray(normalized.data) ? normalized.data : [];
          });
        }

        setTotalCandidatesCount(Number(payload?.total_records || rows.length || 0));
        setShortlistedCount(Number(shortlistedPayload?.total_records || 0));
        setHighMatchQualityCount(Number(highMatchPayload?.total_records || 0));
        setJobCounts(Array.isArray(payload?.job_counts) ? payload.job_counts : []);
        setCandidates(preprocessCandidates(analyticsRows));
      } catch (err) {
        setCandidates([]);
        setTotalCandidatesCount(0);
        setShortlistedCount(0);
        setHighMatchQualityCount(0);
        setJobCounts([]);
        console.error('Failed to fetch candidates:', err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCandidates();
  }, [selectedFilter, customDateRange, isCustomDateRangeReady, resolveCompanyId]);

  // Calculate dynamic analytics data
  const analyticsData = useMemo(() => {
    // Status Distribution
    const statusCounts = {
      new: 0,
      contacted: 0,
      shortlisted: 0,
      interviewed: 0,
      converted: 0
    };
    
    candidates.forEach(c => {
      const status = (c.status || '').toLowerCase();
      if (status === 'new' || status === 'pending') statusCounts.new++;
      else if (status === 'contacted' || status === 'responded') statusCounts.contacted++;
      else if (status === 'shortlisted' || status === 'qualified') statusCounts.shortlisted++;
      else if (status === 'interviewed' || status === 'interview scheduled') statusCounts.interviewed++;
      else if (status === 'converted' || status === 'hired' || status === 'finalised') statusCounts.converted++;
      else statusCounts.new++; // default
    });

    // Channel Performance
    const channelCounts = {
      email: 0,
      linkedin: 0,
      referrals: 0,
      jobPortals: 0
    };
    
    candidates.forEach(c => {
      const source = (c.platforms?.[0] || '').toLowerCase();
      if (source.includes('email')) channelCounts.email++;
      else if (source.includes('linkedin')) channelCounts.linkedin++;
      else if (source.includes('referral')) channelCounts.referrals++;
      else if (source.includes('naukri') || source.includes('indeed') || source.includes('portal')) channelCounts.jobPortals++;
      else channelCounts.linkedin++; // default
    });

    // Candidates by Region - fixed Figma buckets
    const regionCounts = {
      'North America': { count: 0, color: '#5671EA' },
      'Europe': { count: 0, color: '#A1B0F6' },
      'Asia Pacific': { count: 0, color: '#22B88A' },
      'Latin America': { count: 0, color: '#DDE3F8' },
      'Other': { count: 0, color: '#F2B200' },
    };

    candidates.forEach((c) => {
      const location = String(c.location || '').toLowerCase();
      if (!location) {
        regionCounts.Other.count += 1;
        return;
      }

      if (/(usa|united states|canada|mexico|new york|california|texas|toronto|vancouver)/.test(location)) {
        regionCounts['North America'].count += 1;
      } else if (/(uk|united kingdom|england|germany|france|spain|italy|netherlands|europe)/.test(location)) {
        regionCounts.Europe.count += 1;
      } else if (/(india|singapore|australia|china|japan|philippines|vietnam|malaysia|asia|chennai|bangalore|hyderabad|mumbai)/.test(location)) {
        regionCounts['Asia Pacific'].count += 1;
      } else if (/(brazil|argentina|chile|colombia|peru|latin)/.test(location)) {
        regionCounts['Latin America'].count += 1;
      } else {
        regionCounts.Other.count += 1;
      }
    });

    // Performance Metrics
    const totalCandidates = candidates.length;
    const matchScores = candidates.filter(c => c.match_percentage).map(c => Number(c.match_percentage));
    const avgMatchScore = matchScores.length > 0 ? Math.round(matchScores.reduce((a, b) => a + b, 0) / matchScores.length) : 0;
    
    const contactedCount = statusCounts.contacted + statusCounts.interviewed + statusCounts.converted;
    const engagementRate = totalCandidates > 0 ? Math.round((contactedCount / totalCandidates) * 100) : 0;
    
    const interviewedCount = statusCounts.interviewed + statusCounts.converted;
    const interviewConversion = contactedCount > 0 ? Math.round((interviewedCount / contactedCount) * 100) : 0;

    // Calculate donut chart percentages (circumference = 314)
    const totalStatus = Object.values(statusCounts).reduce((a, b) => a + b, 0);
    const statusPercentages = totalStatus > 0 ? {
      new: (statusCounts.new / totalStatus) * 314,
      contacted: (statusCounts.contacted / totalStatus) * 314,
      shortlisted: (statusCounts.shortlisted / totalStatus) * 314,
      interviewed: (statusCounts.interviewed / totalStatus) * 314,
      converted: (statusCounts.converted / totalStatus) * 314
    } : { new: 0, contacted: 0, shortlisted: 0, interviewed: 0, converted: 0 };

    const totalRegion = Object.values(regionCounts).reduce((a, b) => a + b.count, 0);
    const regionPercentages = {};
    let cumulativeOffset = 0;
    
    Object.entries(regionCounts).forEach(([region, data]) => {
      const percentage = totalRegion > 0 ? (data.count / totalRegion) * 314 : 0;
      regionPercentages[region] = {
        dasharray: percentage,
        offset: cumulativeOffset,
        color: data.color,
        count: data.count
      };
      cumulativeOffset += percentage;
    });

    return {
      statusCounts,
      channelCounts,
      regionCounts,
      metrics: {
        avgMatchScore,
        engagementRate,
        interviewConversion
      },
      statusPercentages,
      regionPercentages
    };
  }, [candidates]);

  const getCandidateMeta = (row) => {
    const rawUrl = row.linkedin_profile_url || row.profile_url || '';
    if (!rawUrl) return row.company || row.location || '—';
    try {
      const parsed = new URL(rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`);
      return parsed.hostname.replace(/^www\./, '');
    } catch {
      return String(rawUrl).replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || row.company || row.location || '—';
    }
  };

  // Handler for candidate row click — toggles accordion
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const handleCandidateClick = (candidate) => {
    const id = candidate.id || candidate.email;
    setExpandedCandidateId(expandedCandidateId === id ? null : id);
  };

  // Update candidate data callback for CandidateDetail
  const updateCandidateData = (email, updates) => {
    setCandidates((prev) =>
      prev.map((c) =>
        (c.email || '').toLowerCase() === (email || '').toLowerCase()
          ? { ...c, ...updates }
          : c
      )
    );
  };

  // Render expanded candidate detail inside table accordion
  const renderExpandedCandidate = (candidate) => (
    <CandidateDetail
      candidate={candidate}
      searchId={candidate?.search_id || ''}
      sessionId={candidate?.session_id || ''}
      onBack={() => setExpandedCandidateId(null)}
      onCandidateUpdate={updateCandidateData}
      inline={true}
    />
  );

  return (
    <>
      <AppHeader />
      <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-6">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 xl:flex-row xl:items-center xl:justify-between">
          <h1 className="flex items-center gap-2 text-xl font-bold leading-tight text-gray-800 dark:text-white sm:gap-3 sm:text-2xl">
            <button
              onClick={handleBackNavigation}
              className="w-8 h-8 flex flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
              title={openedFromDashboard ? 'Back to dashboard' : 'Go back'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="min-w-0">Master Database</span>
          </h1>
          <div className="flex min-w-0 flex-col items-start gap-3 xl:items-end">
            {openedFromDashboard && (
              <button
                onClick={handleBackNavigation}
              className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 text-[#3B5BDB] dark:text-indigo-300 bg-[#EEF2FF] dark:bg-indigo-950/50 border border-[#BFCBFF] dark:border-indigo-700 hover:bg-[#E2E8FF] dark:hover:bg-indigo-900/60"
              >
                Back to Dashboard
              </button>
            )}
            <div className="flex w-full max-w-full gap-2 overflow-x-auto pb-1 xl:w-auto xl:overflow-visible xl:pb-0">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm ${
                    selectedFilter === filter
                      ? 'text-black dark:text-white bg-[#12B88626] border border-[#12B886]'
                      : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
            {selectedFilter === 'Custom' && (
              <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto xl:justify-end">
                <input
                  type="date"
                  value={customDateRange.from}
                  onChange={(e) => setCustomDateRange((prev) => ({ ...prev, from: e.target.value }))}
                  max={customDateRange.to || undefined}
                  className="min-w-0 flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 xl:flex-none"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">to</span>
                <input
                  type="date"
                  value={customDateRange.to}
                  onChange={(e) => setCustomDateRange((prev) => ({ ...prev, to: e.target.value }))}
                  min={customDateRange.from || undefined}
                  className="min-w-0 flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 xl:flex-none"
                />
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[500px]">
            <DottedCircleLoader />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading candidates...</p>
          </div>
        ) : (
        <>
        {/* Dashboard Summary Section */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm px-3 py-3 sm:rounded-2xl sm:px-5 sm:py-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#E7ECFF] sm:h-12 sm:w-12 sm:rounded-xl">
                <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none">
                  <path d="M8.5 7V9.5M7.25 8.25H9.75M8.5 14.5V17M7.25 15.75H9.75M14.5 6L16.2143 11.1429L20.5 12.75L16.2143 14.3571L14.5 19.5L12.7857 14.3571L8.5 12.75L12.7857 11.1429L14.5 6Z" stroke="#4B5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <div className="min-w-0">
                <div className="text-xs leading-tight text-gray-500 dark:text-gray-400 sm:text-sm">Total Candidates</div>
                <div className="mt-0.5 text-xl font-bold leading-none text-gray-900 dark:text-white sm:mt-1 sm:text-3xl">{totalCandidatesCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm px-3 py-3 sm:rounded-2xl sm:px-5 sm:py-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#E7ECFF] sm:h-12 sm:w-12 sm:rounded-xl">
                <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3L19 7V12C19 16.5 15.9 20.74 12 21C8.1 20.74 5 16.5 5 12V7L12 3Z" stroke="#4B5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.5 12L11.25 13.75L14.75 10.25" stroke="#4B5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <div className="min-w-0">
                <div className="text-xs leading-tight text-gray-500 dark:text-gray-400 sm:text-sm">Shortlisted</div>
                <div className="mt-0.5 text-xl font-bold leading-none text-gray-900 dark:text-white sm:mt-1 sm:text-3xl">{shortlistedCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm px-3 py-3 sm:rounded-2xl sm:px-5 sm:py-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#E7ECFF] sm:h-12 sm:w-12 sm:rounded-xl">
                <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none">
                  <path d="M12 4L14.4721 9.00617L20 9.81966L16 13.7188L16.9443 19.2254L12 16.625L7.05573 19.2254L8 13.7188L4 9.81966L9.52786 9.00617L12 4Z" stroke="#4B5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <div className="min-w-0">
                <div className="text-xs leading-tight text-gray-500 dark:text-gray-400 sm:text-sm">High Match Quality</div>
                <div className="mt-0.5 text-xl font-bold leading-none text-gray-900 dark:text-white sm:mt-1 sm:text-3xl">{highMatchQualityCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm px-3 py-3 sm:rounded-2xl sm:px-5 sm:py-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#E7ECFF] sm:h-12 sm:w-12 sm:rounded-xl">
                <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none">
                  <path d="M4 18H20M7 15V10M12 15V6M17 15V12" stroke="#4B5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <div className="min-w-0">
                <div className="text-xs leading-tight text-gray-500 dark:text-gray-400 sm:text-sm">Total Roles</div>
                <div className="mt-0.5 text-xl font-bold leading-none text-gray-900 dark:text-white sm:mt-1 sm:text-3xl">{jobCounts.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section - Figma aligned */}
        <div className="mt-6 border-b border-[#E5E7EB] dark:border-slate-700 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-5 items-stretch">
          {/* Status Distribution */}
          <div className="bg-[#F9FAFB] dark:bg-slate-800 border border-[#F3F4F6] dark:border-slate-700 rounded-2xl shadow-[0_4px_63px_rgba(0,0,0,0.06)] dark:shadow-black/20 p-4 w-full min-h-[354px] xl:col-span-2">
            <h3 className="text-[20px] leading-[1.15] font-semibold text-[#364152] dark:text-slate-100 mb-4 break-words">Status Distribution</h3>
            <div className="flex flex-col items-center gap-4">
              {/* Donut Chart SVG */}
              <svg width="120" height="120" viewBox="0 0 120 120" className="flex-shrink-0">
                <circle cx="60" cy="60" r="50" fill="none" stroke={isDark ? '#334155' : '#E5E7EB'} strokeWidth="20"/>
                {/* New */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="#3E55CC" strokeWidth="20" 
                  strokeDasharray={`${analyticsData.statusPercentages.new} 314`} strokeDashoffset="0" transform="rotate(-90 60 60)"/>
                {/* Contacted */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="#99ACF8" strokeWidth="20" 
                  strokeDasharray={`${analyticsData.statusPercentages.contacted} 314`} strokeDashoffset={`-${analyticsData.statusPercentages.new}`} transform="rotate(-90 60 60)"/>
                {/* Shortlisted */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="#18B787" strokeWidth="20" 
                  strokeDasharray={`${analyticsData.statusPercentages.shortlisted} 314`} strokeDashoffset={`-${analyticsData.statusPercentages.new + analyticsData.statusPercentages.contacted}`} transform="rotate(-90 60 60)"/>
                {/* Interviewed */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="#DDE3F8" strokeWidth="20" 
                  strokeDasharray={`${analyticsData.statusPercentages.interviewed} 314`} strokeDashoffset={`-${analyticsData.statusPercentages.new + analyticsData.statusPercentages.contacted + analyticsData.statusPercentages.shortlisted}`} transform="rotate(-90 60 60)"/>
                {/* Converted */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="#F2B200" strokeWidth="20" 
                  strokeDasharray={`${analyticsData.statusPercentages.converted} 314`} strokeDashoffset={`-${analyticsData.statusPercentages.new + analyticsData.statusPercentages.contacted + analyticsData.statusPercentages.shortlisted + analyticsData.statusPercentages.interviewed}`} transform="rotate(-90 60 60)"/>
                {/* Center text */}
                <text x="60" y="50" textAnchor="middle" fontSize="11" fill={isDark ? '#94A3B8' : '#6B7280'} fontWeight="500">Total</text>
                <text x="60" y="64" textAnchor="middle" fontSize="11" fill={isDark ? '#94A3B8' : '#6B7280'} fontWeight="500">Candidates</text>
                <text x="60" y="86" textAnchor="middle" fontSize="22" fill={isDark ? '#F8FAFC' : '#111827'} fontWeight="700">{totalCandidatesCount}</text>
              </svg>
              
              {/* Legend */}
              <div className="w-full flex flex-col gap-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#3E55CC'}}></span>
                    <span className="text-gray-700 dark:text-gray-300">New</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">{analyticsData.statusCounts.new}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#99ACF8'}}></span>
                    <span className="text-gray-700 dark:text-gray-300">Contacted</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">{analyticsData.statusCounts.contacted}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#18B787'}}></span>
                    <span className="text-gray-700 dark:text-gray-300">Shortlisted</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">{analyticsData.statusCounts.shortlisted}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#DDE3F8'}}></span>
                    <span className="text-gray-700 dark:text-gray-300">Interviewed</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">{analyticsData.statusCounts.interviewed}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#F2B200'}}></span>
                    <span className="text-gray-700 dark:text-gray-300">Converted (Hired)</span>
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">{analyticsData.statusCounts.converted}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Channel Performance */}
          <div className="bg-[#F9FAFB] dark:bg-slate-800 border-2 border-[#F3F4F6] dark:border-slate-700 rounded-2xl shadow-[0_4px_63px_rgba(0,0,0,0.06)] dark:shadow-black/20 p-4 w-full min-h-[354px] xl:col-span-3">
            <h3 className="text-[20px] leading-[1.15] font-semibold text-[#364152] dark:text-slate-100 mb-4 break-words">Channel Performance</h3>
            <div className="space-y-3">
              {/* Bar chart */}
              <div className="flex items-end gap-2 h-44 sm:gap-4">
                {/* Email */}
                <div className="flex min-w-0 flex-col items-center flex-1 h-full justify-end">
                  <div className="flex gap-1 items-end h-full">
                    <div className="w-5 bg-yellow-400 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.email / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 100, 100)}%`}} title={`Email: ${analyticsData.channelCounts.email}`}></div>
                    <div className="w-5 bg-yellow-200 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.email / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 40, 100)}%`}} title={`Conversions`}></div>
                  </div>
                  <span className="mt-2 max-w-full text-center text-[10px] leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">Email</span>
                </div>
                {/* LinkedIn */}
                <div className="flex min-w-0 flex-col items-center flex-1 h-full justify-end">
                  <div className="flex gap-1 items-end h-full">
                    <div className="w-5 bg-yellow-400 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.linkedin / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 100, 100)}%`}} title={`LinkedIn: ${analyticsData.channelCounts.linkedin}`}></div>
                    <div className="w-5 bg-yellow-200 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.linkedin / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 40, 100)}%`}} title={`Conversions`}></div>
                  </div>
                  <span className="mt-2 max-w-full text-center text-[10px] leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">LinkedIn</span>
                </div>
                {/* Referrals */}
                <div className="flex min-w-0 flex-col items-center flex-1 h-full justify-end">
                  <div className="flex gap-1 items-end h-full">
                    <div className="w-5 bg-yellow-400 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.referrals / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 100, 100)}%`}} title={`Referrals: ${analyticsData.channelCounts.referrals}`}></div>
                    <div className="w-5 bg-yellow-200 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.referrals / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 40, 100)}%`}} title={`Conversions`}></div>
                  </div>
                  <span className="mt-2 max-w-full text-center text-[10px] leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">Referrals</span>
                </div>
                {/* Job Portals */}
                <div className="flex min-w-0 flex-col items-center flex-1 h-full justify-end">
                  <div className="flex gap-1 items-end h-full">
                    <div className="w-5 bg-yellow-400 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.jobPortals / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 100, 100)}%`}} title={`Job Portals: ${analyticsData.channelCounts.jobPortals}`}></div>
                    <div className="w-5 bg-yellow-200 rounded-t sm:w-10" style={{height: `${Math.min((analyticsData.channelCounts.jobPortals / Math.max(...Object.values(analyticsData.channelCounts), 1)) * 40, 100)}%`}} title={`Conversions`}></div>
                  </div>
                  <span className="mt-2 max-w-full text-center text-[10px] leading-tight text-gray-600 dark:text-gray-400 sm:text-xs">Job Portals</span>
                </div>
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs sm:gap-4">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#F2B200'}}></span>
                  <span className="text-gray-600 dark:text-gray-400">Outreach</span>
                </div>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#A1B0F6'}}></span>
                  <span className="text-gray-600 dark:text-gray-400">Conversion</span>
                </div>
              </div>
            </div>
          </div>

          {/* Candidates by Region */}
          <div className="bg-[#F9FAFB] dark:bg-slate-800 border border-[#F3F4F6] dark:border-slate-700 rounded-2xl shadow-[0_4px_63px_rgba(0,0,0,0.06)] dark:shadow-black/20 p-4 w-full min-h-[354px] xl:col-span-2">
            <h3 className="text-[20px] leading-[1.15] font-semibold text-[#364152] dark:text-slate-100 mb-4 break-words">Candidates by Region</h3>
            <div className="flex flex-col items-center gap-4">
              {/* Donut Chart SVG */}
              <svg width="120" height="120" viewBox="0 0 120 120" className="flex-shrink-0">
                <circle cx="60" cy="60" r="50" fill="none" stroke={isDark ? '#334155' : '#E5E7EB'} strokeWidth="20"/>
                {/* Dynamic region circles */}
                {Object.entries(analyticsData.regionPercentages).map(([region, data]) => (
                  <circle
                    key={region}
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke={data.color}
                    strokeWidth="20"
                    strokeDasharray={`${data.dasharray} 314`}
                    strokeDashoffset={`-${data.offset}`}
                    transform="rotate(-90 60 60)"
                  />
                ))}
                {/* Center text */}
                <text x="60" y="50" textAnchor="middle" fontSize="11" fill={isDark ? '#94A3B8' : '#6B7280'} fontWeight="500">Total</text>
                <text x="60" y="64" textAnchor="middle" fontSize="11" fill={isDark ? '#94A3B8' : '#6B7280'} fontWeight="500">Candidates</text>
                <text x="60" y="86" textAnchor="middle" fontSize="22" fill={isDark ? '#F8FAFC' : '#111827'} fontWeight="700">{totalCandidatesCount}</text>
              </svg>
              
              {/* Legend */}
              <div className="w-full flex flex-col gap-2">
                {Object.entries(analyticsData.regionPercentages).map(([region, data]) => (
                  <div key={region} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: data.color}}></span>
                      <span className="text-gray-700 dark:text-gray-300">{region}</span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">{data.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-[#F9FAFB] dark:bg-slate-800 border-2 border-[#F3F4F6] dark:border-slate-700 rounded-2xl shadow-[0_4px_63px_rgba(0,0,0,0.06)] dark:shadow-black/20 p-4 w-full min-h-[354px] md:col-span-2 xl:col-span-5">
            <h3 className="text-[20px] leading-[1.15] font-semibold text-[#364152] dark:text-slate-100 mb-4">Performance Metrics</h3>
            <div className="space-y-6">
              {/* Average Match Score */}
              <div>
                <div className="flex items-center text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300 w-[130px]">Average Match Score</span>
                </div>
                <div className="w-full h-9 flex items-center">
                  <div className="w-full h-9 bg-slate-200 dark:bg-slate-700 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#1EB386] rounded-sm" style={{width: `${analyticsData.metrics.avgMatchScore}%`}}></div>
                  </div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>
              
              {/* Engagement Rate */}
              <div>
                <div className="flex items-center text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300 w-[130px]">Engagement Rate</span>
                </div>
                <div className="w-full h-9 flex items-center">
                  <div className="w-full h-9 bg-slate-200 dark:bg-slate-700 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#F2B200] rounded-sm" style={{width: `${analyticsData.metrics.engagementRate}%`}}></div>
                  </div>
                </div>
              </div>
              
              {/* Interview Conversion */}
              <div>
                <div className="flex items-center text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300 w-[130px]">Interview Conversion</span>
                </div>
                <div className="w-full h-9 flex items-center">
                  <div className="w-full h-9 bg-slate-200 dark:bg-slate-700 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#6E87F0] rounded-sm" style={{width: `${analyticsData.metrics.interviewConversion}%`}}></div>
                  </div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#364152] dark:text-slate-300">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1EB386]"></span>Average Match Score</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#F2B200]"></span>Engagement Rate</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#6E87F0]"></span>Interview Conversion</span>
              </div>
            </div>
          </div>
        </div>
        </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 md:p-6 mt-8">
            {/* TableComponent for candidate list (only) */}
            <TableComponent
              data={candidates}
              serverSide={true}
              serverFetchData={fetchCandidatesServer}
              columns={[{
                label: 'CANDIDATE',
                key: 'name',
                sortable: true,
                width: '340px',
                minWidth: '340px',
                render: (val, row) => {
                  return (
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 p-[3px]" style={{ border: '2px solid #F59E0B', background: '#FEF3C7' }}>
                        <img
                          src="/social_icons/candidate_profile.png"
                          alt={row.name || 'User'}
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                      <div className="min-w-0 flex-1 leading-tight">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.name || 'Unknown'}</div>
                        <div className="text-xs text-gray-700 dark:text-gray-300 truncate">
                          {row.title || '—'}
                        </div>
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 truncate lowercase">
                          {getCandidateMeta(row)}
                        </div>
                      </div>
                    </div>
                  );
                }
              },
                {
                  label: 'MATCH',
                  key: 'match_percentage',
                  sortable: true,
                  width: '72px',
                  render: (val) => {
                    let color = 'text-green-600';
                    if (typeof val === 'number') {
                      if (val >= 90) color = 'text-green-600';
                      else if (val >= 80) color = 'text-orange-500';
                      else color = 'text-red-500';
                    }
                    return (
                      <span className={`font-semibold text-base ${color}`}>{val != null ? `${val}%` : '-'}</span>
                    );
                  }
                },
                {
                  label: 'SOURCE',
                  key: 'platforms',
                  sortable: false,
                  width: '64px',
                  render: (val, row) => {
                    let platform = '-';
                    if (row.platforms === undefined || row.platforms === null) return platform;
                    if (Array.isArray(row.platforms)) {
                      platform = row.platforms.length > 0 ? row.platforms[0] : '-';
                    } else if (typeof row.platforms === 'string') {
                      platform = row.platforms;
                    } else if (typeof row.platforms === 'object') {
                      const values = Object.values(row.platforms);
                      platform = values.length > 0 ? values[0] : '-';
                    }
                    if (typeof platform === 'string') {
                      if (platform.toLowerCase().includes('linkedin')) {
                        return (
                          <span title="LinkedIn" style={{display:'inline-flex',alignItems:'center',gap:'4px'}}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#0A66C2" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign:'middle'}}>
                              <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.27c-.966 0-1.75-.79-1.75-1.76 0-.97.784-1.76 1.75-1.76s1.75.79 1.75 1.76c0 .97-.784 1.76-1.75 1.76zm13.5 11.27h-3v-5.6c0-1.34-.03-3.07-1.87-3.07-1.87 0-2.16 1.46-2.16 2.97v5.7h-3v-10h2.89v1.36h.04c.4-.76 1.38-1.56 2.84-1.56 3.04 0 3.6 2 3.6 4.59v5.61z"/>
                            </svg>
                          </span>
                        );
                      }
                      if (platform.toLowerCase().includes('twitter')) {
                        return (
                          <span title="Twitter" style={{display:'inline-flex',alignItems:'center',gap:'4px'}}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#1DA1F2" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign:'middle'}}>
                              <path d="M24 4.557a9.93 9.93 0 0 1-2.828.775 4.932 4.932 0 0 0 2.165-2.724c-.951.564-2.005.974-3.127 1.195a4.92 4.92 0 0 0-8.384 4.482c-4.086-.205-7.713-2.164-10.141-5.144a4.822 4.822 0 0 0-.664 2.475c0 1.708.87 3.216 2.188 4.099a4.904 4.904 0 0 1-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.936 4.936 0 0 1-2.224.084c.627 1.956 2.444 3.377 4.6 3.417a9.867 9.867 0 0 1-6.102 2.104c-.396 0-.787-.023-1.175-.069a13.945 13.945 0 0 0 7.548 2.212c9.057 0 14.009-7.514 14.009-14.009 0-.213-.005-.425-.014-.636a10.012 10.012 0 0 0 2.457-2.548z"/>
                            </svg>
                          </span>
                        );
                      }
                      if (platform.toLowerCase().includes('naukri')) {
                        return (
                          <span title="Naukri" style={{display:'inline-flex',alignItems:'center',gap:'4px'}}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#0086C3" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign:'middle'}}>
                              <circle cx="12" cy="12" r="10" fill="#0086C3" />
                              <text x="12" y="16" textAnchor="middle" fontSize="10" fill="#fff" fontFamily="Arial" fontWeight="bold">N</text>
                            </svg>
                          </span>
                        );
                      }
                    }
                    return platform;
                  }
                },
                {
                  label: 'STATUS',
                  key: 'status',
                  sortable: false,
                  minWidth: '96px',
                  render: (val) => {
                    const cfg = getOverviewStatusConfig(val);
                    return (
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cfg.custom ? '' : cfg.bg + ' ' + cfg.text}`}
                        style={cfg.custom ? { backgroundColor: cfg.bgStyle, color: cfg.textStyle } : {}}>
                        {cfg.label}
                      </span>
                    );
                  }
                },
                {
                  label: 'RESUME STATS',
                  key: 'resume_stats',
                  sortable: true,
                  minWidth: '120px',
                  render: (val, row) => {
                    const hasResume = !!(row.doc_id || row.cv_filename);
                    return hasResume ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                        Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">
                        Not Submitted
                      </span>
                    );
                  }
                },
                {
                  label: 'EXPERIENCE',
                  key: 'experience',
                  sortable: false,
                  width: '92px',
                  render: (val, row) => {
                    const formatYears = (yearsValue) => {
                      const numeric = Number(yearsValue);
                      if (!Number.isFinite(numeric) || numeric < 0) return null;
                      return `${Math.round(numeric)} years`;
                    };

                    const parseExperienceStringToYears = (input) => {
                      const text = String(input || '').toLowerCase().trim();
                      if (!text || text.includes('empty') || text.includes('no specific')) return null;
                      const yearMatch = text.match(/(\d+(?:\.\d+)?)\s*\+?\s*year/);
                      const monthMatch = text.match(/(\d+(?:\.\d+)?)\s*month/);
                      const plainNumber = text.match(/^(\d+(?:\.\d+)?)$/);

                      let totalYears = 0;
                      if (yearMatch) totalYears += Number(yearMatch[1]);
                      if (monthMatch) totalYears += Number(monthMatch[1]) / 12;
                      if (!yearMatch && !monthMatch && plainNumber) totalYears = Number(plainNumber[1]);

                      return totalYears > 0 ? formatYears(totalYears) : null;
                    };

                    if (row.experience_years) {
                      const normalized = parseExperienceStringToYears(row.experience_years);
                      if (normalized) return normalized;
                    }
                    if (row.years_of_experience) {
                      const normalized = parseExperienceStringToYears(row.years_of_experience);
                      if (normalized) return normalized;
                    }
                    if (Array.isArray(row.experience) && row.experience.length > 0) {
                      const durationString = row.experience.find(
                        (e) => typeof e === 'object' && e !== null && typeof e.duration === 'string' && e.duration.trim() !== '' && !e.duration.toLowerCase().includes('empty')
                      );
                      if (durationString?.duration) {
                        const normalized = parseExperienceStringToYears(durationString.duration);
                        if (normalized) return normalized;
                      }
                      let totalYears = 0;
                      row.experience.forEach(exp => {
                        if (typeof exp === 'object' && exp !== null) {
                          if (exp.start_date) {
                            const start = new Date(exp.start_date);
                            const end = !exp.end_date || exp.end_date === 'Present' || exp.end_date === 'Current'
                              ? new Date()
                              : new Date(exp.end_date);
                            if (!isNaN(start) && !isNaN(end)) {
                              totalYears += (end - start) / (365.25 * 24 * 60 * 60 * 1000);
                            }
                          }
                        }
                      });
                      if (totalYears > 0) {
                        return formatYears(totalYears);
                      }
                    }
                    return '—';
                  }
                },
                {
                  label: 'TAGS',
                  key: 'tags',
                  sortable: false,
                  minWidth: '100px',
                  render: (val, row) => {
                    const tags = row.skills || [];
                    if (tags.length === 0) return '—';
                    return (
                      <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200 whitespace-nowrap">
                          {tags[0]}
                        </span>
                        {tags.length > 1 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium text-gray-500 bg-gray-50 whitespace-nowrap" title={tags.slice(1).join(', ')}>
                            +{tags.length - 1}
                          </span>
                        )}
                      </div>
                    );
                  }
                },
                {
                  label: 'JOB TITLE',
                  key: 'headline',
                  sortable: true,
                  minWidth: '160px',
                  render: (val, row) => (
                    <span className="text-xs text-gray-700 dark:text-gray-300 truncate block" title={row.headline || row.title || '—'}>
                      {row.headline || row.title || '—'}
                    </span>
                  )
                },
                {
                  label: 'LAST ACTION',
                  key: 'lastAction',
                  sortable: false,
                  minWidth: '110px',
                  render: (val) => <span className="text-xs text-gray-700">{val || '—'}</span>
                },
                {
                  label: 'NEXT STEP',
                  key: 'nextStep',
                  sortable: false,
                  minWidth: '110px',
                  render: (val) => <span className="text-xs font-medium text-blue-600">{val || '—'}</span>
                },
                {
                  label: 'CONTACT',
                  key: 'contact',
                  sortable: false,
                  minWidth: '80px',
                  render: (val, row) => {
                    const icons = [];
                    if (row.linkedin_profile_url) {
                      icons.push(
                        <a
                          href={row.linkedin_profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-7 h-7 rounded flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                          title="LinkedIn Profile"
                          onClick={e => e.stopPropagation()}
                          key="linkedin"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </a>
                      );
                    }
                    if (row.email) {
                      icons.push(
                        <a
                          href={`mailto:${row.email}`}
                          className="w-7 h-7 rounded flex items-center justify-center bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          title={row.email}
                          onClick={e => e.stopPropagation()}
                          key="email"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                        </a>
                      );
                    }
                    return icons.length > 0 ? <div className="flex items-center gap-2">{icons}</div> : '—';
                  }
                },
                {
                  label: 'LAST UPDATED',
                  key: 'lastUpdated',
                  sortable: false,
                  width: '92px',
                  render: (val, row) => {
                    if (!row.updatedAt && !val) return '—';
                    const date = row.updatedAt || val;
                    const d = new Date(date);
                    if (isNaN(d.getTime())) return '—';
                    const now = new Date();
                    const diff = Math.floor((now - d) / 1000);
                    if (diff < 60) return `${diff}s ago`;
                    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
                    return d.toLocaleDateString();
                  }
                },
              ]}
              filters={tabs.map(tab => ({ key: tab.toLowerCase(), label: tab, filterKey: 'status', matchValue: tab }))}
              title={`Candidate List - ${selectedJobPostingFilter || 'All'}`}
              showPagination={true}
              itemsPerPage={10}
              disableHorizontalScroll={true}
              compactMode={true}
              queryKey={`${selectedFilter}:${customDateRange.from || ''}:${customDateRange.to || ''}`}
              initialCurrentPage={tableCurrentPage}
              onPageChange={setTableCurrentPage}
              showJobFilterDropdown={true}
              jobFilterOptions={jobFilterOptions}
              onJobFilterChange={setSelectedJobPostingFilter}
              onRowToggle={handleCandidateClick}
              expandedRowId={expandedCandidateId}
              renderExpandedRow={renderExpandedCandidate}
            />
          </div>
        </>
        )}
      </div>
    </>
  );
};

export default MasterDatabase;
